/**
 * HTML Renderer Utility
 * Safely renders HTML content with sanitization and fallbacks
 */

/**
 * Safely render HTML content with basic sanitization
 * @param {string} htmlString - HTML string to render
 * @returns {Object|null} - React dangerouslySetInnerHTML props or null
 */
export const renderHTMLContent = (htmlString) => {
  if (!htmlString) return null;
  
  try {
    // For streaming content, we want to preserve all styles
    // Don't sanitize aggressively - just remove potentially dangerous elements
    const sanitizedHTML = htmlString
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '') // Remove iframe tags
      .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '') // Remove event handlers
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/vbscript:/gi, ''); // Remove vbscript: protocol
    
    return { __html: sanitizedHTML };
  } catch (error) {
    console.error('Error rendering HTML content:', error);
    return { __html: htmlString };
  }
};

/**
 * Test HTML rendering to verify content is valid
 * @param {string} htmlString - HTML string to test
 * @returns {Object} - Test result object
 */
export const testHTMLRendering = (htmlString) => {
  if (!htmlString) return { success: false, error: 'No HTML content' };
  
  try {
    // Create a temporary div to test rendering
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    
    // Check if the content was parsed correctly
    const hasContent = tempDiv.children.length > 0 || tempDiv.textContent.trim().length > 0;
    
    return {
      success: true,
      childrenCount: tempDiv.children.length,
      textContent: tempDiv.textContent.substring(0, 100) + '...',
      hasContent: hasContent,
      htmlLength: htmlString.length
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Extract text content from HTML string
 * @param {string} htmlString - HTML string to extract text from
 * @returns {string} - Plain text content
 */
export const extractTextFromHTML = (htmlString) => {
  if (!htmlString) return '';
  
  try {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    return tempDiv.textContent || tempDiv.innerText || '';
  } catch (error) {
    return htmlString;
  }
};

/**
 * Check if string contains HTML tags
 * @param {string} str - String to check
 * @returns {boolean} - True if contains HTML tags
 */
export const containsHTML = (str) => {
  if (!str || typeof str !== 'string') return false;
  return /<[^>]*>/g.test(str);
};

/**
 * Get HTML content length (excluding tags)
 * @param {string} htmlString - HTML string to measure
 * @returns {number} - Text content length
 */
export const getHTMLContentLength = (htmlString) => {
  if (!htmlString) return 0;
  return extractTextFromHTML(htmlString).length;
};
